import { useState, type ChangeEvent } from 'react';
import type { UserIdentity } from '@supabase/supabase-js';
import { useAuth, getCachedSession } from '../context/AuthContext.js';
import { getProjectGithubInstallUrl, type ProjectSummary } from '../api.js';
import { clearGitHubConnectionReturn, rememberGitHubConnection, saveGitHubConnection } from '../lib/github-connection.js';

export function GitHubConnection({ projectId, projects = [] }: { projectId?: string; projects?: ProjectSummary[] }) {
  const { user, session, signInWithGitHub } = useAuth();
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(new URLSearchParams(window.location.search).get('githubConnection') === 'failed'
    ? 'GitHub connection did not complete. Reconnect the same account or use a repository access token.' : '');
  const linked = user?.identities?.some((identity: UserIdentity) => identity.provider === 'github');
  const sameSubscriber = (): boolean => !!user && !!session && session.user.id === user.id
    && getCachedSession()?.user.id === user.id;
  const save = async (): Promise<void> => {
    if (!user || !session || !token.trim() || !sameSubscriber()) return;
    setBusy(true); setMessage('');
    try {
      await saveGitHubConnection(token.trim(), session, user.id);
      setToken('');
      if (!sameSubscriber()) { setMessage('Account changed. Reconnect GitHub for your current account.'); return; }
      setMessage('GitHub credential verified. Select your project, then connect its GitHub App installation.');
    } catch {
      setMessage('GitHub connection could not be verified. Retry or use a repository access token.');
    } finally { setBusy(false); }
  };
  const reconnect = async (): Promise<void> => {
    if (!user || !linked || !sameSubscriber()) return;
    setBusy(true); setMessage('');
    try { rememberGitHubConnection(user.id, projectId); await signInWithGitHub(); }
    catch {
      clearGitHubConnectionReturn();
      setMessage('GitHub sign-in could not start. Use a repository access token or retry.');
      setBusy(false);
    }
  };
  const install = async (): Promise<void> => {
    if (!projectId || !sameSubscriber()) return;
    setBusy(true); setMessage('');
    try {
      const { url } = await getProjectGithubInstallUrl(projectId);
      if (!sameSubscriber()) throw new Error('subscriber_changed');
      const target = new URL(url);
      if (target.origin !== 'https://github.com' || !/^\/apps\/[^/]+\/installations\/new$/.test(target.pathname)
        || !target.searchParams.get('state')) throw new Error('invalid_install_url');
      window.location.assign(target.href);
    } catch {
      setMessage('Verify your GitHub credential and repository access first, then retry this project installation.');
      setBusy(false);
    }
  };
  return <div>
    <p>Connect your GitHub account, then authorize the App for your project. GitHub connections are available separately from inference BYOK.</p>
    {linked && <button className="btn btn-ghost" disabled={busy || !session} onClick={() => void reconnect()}>Reconnect GitHub account</button>}
    <label>GitHub repository access token<input className="input" type="password" autoComplete="off" aria-label="GitHub repository access token" value={token} disabled={busy} onChange={(event: ChangeEvent<HTMLInputElement>) => setToken(event.target.value)} /></label>
    <button className="btn btn-primary" disabled={busy || !token.trim() || !user || !session} onClick={() => void save()}>Verify GitHub connection</button>
    <p>Use a token that can access your repository. Connecting the App opens GitHub to choose repository access.</p>
    {projects.length > 0 && <label>GitHub project<select aria-label="GitHub project" value={projectId ?? ''} onChange={(event: ChangeEvent<HTMLSelectElement>) => window.location.assign('/?page=config&projectId=' + encodeURIComponent(event.target.value))}>
      <option value="" disabled>Select a project</option>
      {projects.map((project: ProjectSummary) => <option key={project.project_id} value={project.project_id}>{project.display_name}</option>)}
    </select></label>}
    <a href="/?page=addproject">Create or add a repository project</a>
    <button className="btn btn-ghost" disabled={busy || !projectId || !user || !session} onClick={() => void install()}>Connect GitHub App</button>
    {message && <p role="status">{message}</p>}
  </div>;
}
